import { useEffect, useState } from "react";
import { Country, fetchCountries } from "../redux/countryActions";
import { styled, Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

interface CountrySelectProps {
    label?: string;
    defaultValue?: number;
    required?: boolean;
    id?: string;
    name?: string;
    fullWidth?: boolean;
    value: Country | null;
    width?: string | number;
    sx?: any;
    disabledCountryCodes?: string[];
    errorMessage?: string;
    onChange: (value: Country) => void;
}

const CountrySelect = ({
    label,
    value,
    defaultValue,
    required = false,
    id = "country",
    name = "country",
    fullWidth = true,
    width,
    sx,
    disabledCountryCodes = [],
    errorMessage,
    onChange,
}: CountrySelectProps) => {
    const [countries, setCountries] = useState<Country[]>([]);
    const { t } = useTranslation();

    useEffect(() => {
        const loadCountries = async () => {
            const data = await fetchCountries();
            setCountries(
                data
                    .filter(
                        (country) =>
                            !disabledCountryCodes.includes(country.code)
                    )
                    .map((country) => ({
                        ...country,
                        name:
                            t(
                                `country.${country.name
                                    .toLowerCase()
                                    .replace(/\s+/g, "")}`
                            ) ?? country.name,
                    }))
            );

            const matchedCountry = data.find((c) => c.id === defaultValue);

            if (matchedCountry) {
                onChange(matchedCountry);
            }
        };

        loadCountries();
    }, [defaultValue]);

    return (
        <Autocomplete
            options={countries}
            getOptionLabel={(option) => option.name}
            value={value}
            onChange={(_, newValue) => {
                if (newValue) {
                    onChange(newValue);
                }
            }}
            renderInput={(params) => (
                <Input
                    {...params}
                    label={label || t("inputLabels.country")}
                    required={required}
                    id={id}
                    name={name}
                    fullWidth={fullWidth}
                    error={!!errorMessage}
                    helperText={errorMessage}
                    sx={{ width, ...sx }}
                    inputProps={{
                        ...params.inputProps,
                        autoComplete: "country",
                    }}
                />
            )}
        />
    );
};

export default CountrySelect;

const Input = styled(TextField)({
    backgroundColor: "white",
    borderRadius: "5px",
});
